import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { assignedProgress } from 'components/MyTasks/myTasksTableModel';
import { useIncidentMyTasks } from 'components/MyTasks/useMyTasks';

type MyAssignedActionsSummaryProps = {
  incidentId?: string | null;
  className?: string;
};

/**
 * Assigned-to-you complete / incomplete / overdue, for the Actions toolbar.
 * Overdue is a subset of incomplete. Percents are of the assigned total.
 */
const MyAssignedActionsSummary: React.FC<MyAssignedActionsSummaryProps> = ({
  incidentId,
  className,
}) => {
  const { tasks, isLoading } = useIncidentMyTasks(incidentId ?? undefined);
  const progress = useMemo(() => assignedProgress(tasks), [tasks]);

  if (isLoading && progress.total === 0) {
    return (
      <p
        className={cn('tw:text-xs tw:text-muted-foreground', className)}
        aria-busy="true"
      >
        Loading your actions…
      </p>
    );
  }

  if (progress.total === 0) {
    return (
      <p className={cn('tw:text-xs tw:text-muted-foreground', className)}>
        None assigned to you
      </p>
    );
  }

  return (
    <p
      className={cn(
        'tw:text-xs tw:tabular-nums tw:text-muted-foreground',
        className
      )}
      aria-label={`Yours: ${progress.complete} of ${progress.total} complete (${progress.completePct} percent), ${progress.incomplete} incomplete (${progress.incompletePct} percent), ${progress.overdue} overdue (${progress.overduePct} percent)`}
    >
      <span className="tw:font-medium tw:text-foreground">Yours</span>
      {` ${progress.complete}/${progress.total} complete (${progress.completePct}%)`}
      <span aria-hidden> · </span>
      {`${progress.incomplete} incomplete (${progress.incompletePct}%)`}
      <span aria-hidden> · </span>
      <span
        className={progress.overdue > 0 ? 'tw:text-destructive' : undefined}
      >
        {`${progress.overdue} overdue (${progress.overduePct}%)`}
      </span>
    </p>
  );
};

export default MyAssignedActionsSummary;
